/**
 * Accept a challenge invite from the Home Invite tile (specs/home-page AC-27). Same contract as the
 * Arena card's accept path (coin check, server accept, XP + coin sync, list refetch), without the
 * card's prediction plumbing.
 */

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { acceptChallenge } from '../../challenges/helpers/challengeCardApi';
import { useInvalidateXpSummary } from '../../economy/hooks/useXpSummary';
import { hasEnoughCoins } from '../../shared/helpers/coins.helper';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useSnackbar } from '../../shared/hooks/useSnackbar';
import { HOME_COPY } from '../constants/home.constants';

export interface UseAcceptInviteReturn {
  /** Resolves true when the invite was accepted. */
  accept: (challengeId: string, stakeCoins: number) => Promise<boolean>;
  /** The challenge currently being accepted, for the button's busy state. */
  acceptingId: string | null;
}

export const useAcceptInvite = (): UseAcceptInviteReturn => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const invalidateXp = useInvalidateXpSummary();
  const { showSnackbar } = useSnackbar();
  const [acceptingId, setAcceptingId] = useState<string | null>(null);

  const accept = useCallback(
    async (challengeId: string, stakeCoins: number): Promise<boolean> => {
      const coins = useEconomyStore.getState().coins;
      if (!hasEnoughCoins(coins, stakeCoins)) {
        showSnackbar(HOME_COPY.needCoins(stakeCoins), 'error');
        return false;
      }
      setAcceptingId(challengeId);
      try {
        const result = await acceptChallenge(challengeId, apiClient);
        if (!result.success) {
          showSnackbar(result.error || HOME_COPY.ACCEPT_FAILED, 'error');
          return false;
        }
        // The stake leaves the balance on accept; XP lands server-side, so refetch both.
        if (stakeCoins > 0) {
          useEconomyStore.getState().syncCoins(useEconomyStore.getState().coins - stakeCoins);
        }
        invalidateXp();
        void queryClient.invalidateQueries({ queryKey: ['challenges'] });
        showSnackbar(HOME_COPY.ACCEPTED, 'success');
        return true;
      } finally {
        setAcceptingId(null);
      }
    },
    [apiClient, invalidateXp, queryClient, showSnackbar],
  );

  return { accept, acceptingId };
};
